/**
 * Base route of the Custom Tests 2.0 experience. The classic (V1) pages own
 * `/custom-tests` (the side-nav destination); V2 lives at its own URL and is
 * reached via the banner on the classic lookup page.
 */
export const CUSTOM_TESTS_V2_BASE = '/custom-tests-v2';

export type CustomTestListItem = {
    CategoryId: number;
    CategoryName: string;
    CreatedBy: string;
    DateCreate: string;
    Id: number;
    IsActive: boolean;
    MinScore: number;
    Mode: string;
    ModeId: number;
    Name: string;
    NumQuestions: number;
    ProductId: number;
    ProductName: string;
};

// === Detail (single test for edit) ===

export type CustomTestDetail = {

    /** Distinct assignment targets (access-code transactions + classrooms). */
    AssignmentCount: number;
    CategoryId: number;
    CreatedBy: string;
    DateCreate: string;

    /** True only when active, unassigned/untaken, AND owned by the current user. */
    Editable: boolean;
    HasApplicationQuestions: boolean;
    HasResults: boolean;
    Id: number;
    IsActive: boolean;

    /** True when the current user created this test. */
    IsOwner: boolean;
    LanguageId: number;
    LifecycleState: CustomTestLifecycleState;
    MinScore: number;
    Minutes: number;
    Mode: string;

    /** 1 = Training, 2 = Testing, 3 = Both (linked mode pair). */
    ModeId: number;
    MultiProductTest: boolean;
    Name: string;
    NumQuestions: number;
    Owner: string;
    OwnerId: number;
    ProductIds: number[];

    /** 1 = randomized delivery order, 2 = curriculum order, 0 = unspecified (legacy). */
    QuestionOrderMode: number;
    QuestionsXml: string;
    Randomly: number;
    SelectedQuestionIds: string;

    // Stored delivery/question-pool type: 1 = In-App, 3 = In-Browser, 11 = mixed/LITA.
    TestType: number;
    UseRandomly: boolean;
};

// === Lifecycle (Custom Tests 2.0) ===

/**
 * Derived lifecycle state for a Custom Test. A dedicated DB column is planned as a
 * follow-up; for now these values are derived server-side from IsActive + assignment
 * usage (HasResults) so the UI can reason about editability consistently.
 */
export type CustomTestLifecycleState = 'draft' | 'active' | 'assigned' | 'archived';

// === Wizard (Create / Edit / Copy) ===

/**
 * Entry mode for the Custom Test authoring wizard. Drives which steps are shown and
 * whether Save creates a new record (create/copy) or updates an existing one (edit).
 */
export type CustomTestWizardMode = 'create-smart' | 'create-manual' | 'copy' | 'edit';

/**
 * Prefill payload used to seed a copy draft without persisting anything server-side.
 * The original test is never modified; Save creates a brand-new owned test.
 */
export type CustomTestCopyData = {
    CategoryId: number;
    HasApplicationQuestions: boolean;
    LanguageId: number;
    MinScore: number;
    Minutes: number;
    ModeId: number;
    Name: string;
    ProductIds: number[];
    QuestionOrderMode: number;
    QuestionsXml: string;
    Randomly: number;
    SelectedQuestionIds: string;
    SourceTestId: number;
    UseRandomly: boolean;
};

// === Smart Build (recommendation engine — spec §13.1) ===

export type SmartBuildRequest = {
    categoryId: number;

    /** Slot 1 Classroom (§8.2): scopes performance/activity metric context. */
    classroomId: number;

    /** Hybrid-category question bank (in-app vs browser) — must match the Questions step. */
    deliveryType?: number;

    /** Question-pool language (§10.3) — must match what the Questions step fetches. */
    languageId?: number;

    /** Single Program's product (Smart Build requires a single program). */
    productIds: number[];

    /** Purpose key (§9.4): weak-skills | coverage-gaps | recent-activity | selected-content | readiness | mastery. */
    purpose: string;

    /** Slot 3 Scope Type: 0 = All Available Content, 1 = Topic (Theme), 2 = Objective. */
    scopeType: number;

    /** Slot 3 Scope Values: Theme ids or Objective ids. */
    scopeValueIds: number[];

    /** Slot 4 Size: resolved question count (§11). */
    size: number;
};

export type SmartBuildRepresentedObjective = {
    ObjectiveId: number;
    ObjectiveName: string;
};

export type SmartBuildViability = {
    ActiveObjectives: number;
    ActiveStudents: number;
    EligibleQuestionTotal: number;
    EnrolledStudents: number;
    ObjectivesInScope: number;
    Pass: boolean;
    RequiredActiveObjectives: number;
    RequiredActiveStudents: number;
    RequiredTotalActivity: number;

    /** none | low | sufficient (§12.4). */
    State: string;
    TotalActivity: number;
};

export type SmartBuildRecommendation = {
    Message: string;
    Quality: 'high' | 'medium' | 'low';
    RepresentedObjectives: SmartBuildRepresentedObjective[];
    SelectedQuestionIds: string;
    SuggestedModeId: number;
    SuggestedOrderMode: string;
    TotalRecommended: number;
    Viability: SmartBuildViability;
};

// === Categories and Products (for pickers) ===

export type CustomTestCategory = {
    Color: string;
    IconURL: string;
    Id: number;
    Name: string;
};

export type CustomTestProduct = {
    Color: string;
    IconURL: string;
    Id: number;
    Name: string;
};

export type CustomTestLanguage = {
    Id: number;
    Name: string;
};